
import { Button } from "@/components/ui/button";
import { Calendar, Users, Shield, ArrowRight, Heart, Globe, Clock, Search, Tag, Eye, Gift, Book, Link as LinkIcon, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TimelineDemo from "@/components/landing/TimelineDemo";
import LandingHeader from "@/components/landing/LandingHeader";
import StaticHeroBackground from "@/components/landing/StaticHeroBackground";
import { useAuth } from "@/contexts/AuthContext";

const LandingPage = () => {
  const { t, i18n } = useTranslation(['landing', 'common']);
  const { user, loading } = useAuth();
  const currentLanguage = i18n.language;
  const isEnglish = currentLanguage === 'en';

  const handleMainCTA = () => {
    if (user) {
      // Redirect authenticated users to app
      window.location.href = 'https://app.uplivion.life/dashboard';
    } else {
      // Redirect non-authenticated users to registration
      window.location.href = 'https://app.uplivion.life/register';
    }
  };

  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <LandingHeader />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-32 lg:py-40 overflow-hidden">
          <StaticHeroBackground />
          
          <div className="container mx-auto text-center px-6 max-w-4xl relative z-10">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-8 text-gray-900 leading-tight tracking-tight"
              dangerouslySetInnerHTML={{ __html: t('landing:hero.title') }}
            />
            <p className="text-xl md:text-2xl mb-8 text-gray-600 font-normal leading-relaxed max-w-3xl mx-auto">
              {t('landing:hero.subtitle')}
            </p>
            
            {/* Benefits List */}
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12">
              <div className="flex items-center gap-3 text-left">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{t('landing:hero.benefits.chronological')}</span>
              </div>
              <div className="flex items-center gap-3 text-left">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{t('landing:hero.benefits.structured')}</span>
              </div>
              <div className="flex items-center gap-3 text-left">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{t('landing:hero.benefits.collaborative')}</span>
              </div>
              <div className="flex items-center gap-3 text-left">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{t('landing:hero.benefits.legacy')}</span>
              </div>
            </div>
            
            <div className="flex justify-center">
              {loading ? (
                <div className="w-64 h-14 bg-gray-200 animate-pulse rounded-xl"></div>
              ) : (
                <Button 
                  onClick={handleMainCTA}
                  className="bg-uplivion-blue hover:bg-blue-700 text-white text-lg px-10 py-4 h-auto rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
                >
                  <span className="flex items-center gap-2">
                    {user ? 'Zur App' : t('landing:hero.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 
                className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight"
                dangerouslySetInnerHTML={{ __html: t('landing:features.title') }}
              />
              <p className="text-xl text-gray-600 font-normal max-w-2xl mx-auto">
                {t('landing:features.subtitle')}
              </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
              <div className="group p-10 rounded-3xl bg-white/70 backdrop-blur-sm shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-uplivion-blue/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-uplivion-blue/20 transition-colors">
                  <Calendar className="w-8 h-8 text-uplivion-blue" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('landing:features.timeline.title')}</h3>
                <p className="text-gray-600 leading-relaxed font-normal">
                  {t('landing:features.timeline.description')}
                </p>
              </div>
              
              <div className="group p-10 rounded-3xl bg-white/70 backdrop-blur-sm shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-uplivion-orange/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-uplivion-orange/20 transition-colors">
                  <Heart className="w-8 h-8 text-uplivion-orange" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('landing:features.memories.title')}</h3>
                <p className="text-gray-600 leading-relaxed font-normal">
                  {t('landing:features.memories.description')}
                </p>
              </div>
              
              <div className="group p-10 rounded-3xl bg-white/70 backdrop-blur-sm shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-purple-500/20 transition-colors">
                  <Gift className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('landing:features.gifts.title')}</h3>
                <p className="text-gray-600 leading-relaxed font-normal">
                  {t('landing:features.gifts.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                {t('landing:useCases.title')}
              </h2>
              <p className="text-xl text-gray-600 font-normal max-w-2xl mx-auto">
                {t('landing:useCases.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {/* Memorial Timeline */}
              <div className="group p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-100 transition-colors">
                  <Heart className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{t('landing:useCases.memorial.title')}</h3>
                <p className="text-gray-600 leading-relaxed italic">
                  "{t('landing:useCases.memorial.description')}"
                </p>
              </div>

              {/* Gift Timeline */}
              <div className="group p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-100 transition-colors">
                  <Gift className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{t('landing:useCases.gift.title')}</h3>
                <p className="text-gray-600 leading-relaxed italic">
                  "{t('landing:useCases.gift.description')}"
                </p>
              </div>

              {/* Legacy Timeline */}
              <div className="group p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Users className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{t('landing:useCases.legacy.title')}</h3>
                <p className="text-gray-600 leading-relaxed italic">
                  "{t('landing:useCases.legacy.description')}"
                </p>
              </div>

              {/* Personal Chronicle */}
              <div className="group p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-100 transition-colors">
                  <Book className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{t('landing:useCases.personal.title')}</h3>
                <p className="text-gray-600 leading-relaxed italic">
                  "{t('landing:useCases.personal.description')}"
                </p>
              </div>

              {/* Future Integration */}
              <div className="group p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 lg:col-span-2">
                <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-orange-100 transition-colors">
                  <LinkIcon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{t('landing:useCases.future.title')}</h3>
                <p className="text-gray-600 leading-relaxed italic">
                  "{t('landing:useCases.future.description')}"
                </p>
              </div>
            </div>
          </div>
        </section>

        <TimelineDemo />

        {/* Differentiation Section */}
        <section className="py-24 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                {t('landing:differentiation.title')}
              </h2>
              <p className="text-xl text-gray-600 font-normal max-w-2xl mx-auto">
                {t('landing:differentiation.subtitle')}
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-2 bg-gray-50 p-6 border-b border-gray-200">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900">{t('landing:differentiation.comparison.socialMedia')}</h3>
                    <p className="text-sm text-gray-600 mt-1">Facebook, Instagram, etc.</p>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-uplivion-blue">{t('landing:differentiation.comparison.uplivion')}</h3>
                    <p className="text-sm text-uplivion-blue/70 mt-1">Chronological Life Archive</p>
                  </div>
                </div>
                
                {/* Comparison Rows */}
                <div className="divide-y divide-gray-100">
                  <div className="grid grid-cols-2 p-6">
                    <div className="pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700">{t('landing:differentiation.comparison.feed.social')}</span>
                      </div>
                    </div>
                    <div className="pl-4 border-l border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{t('landing:differentiation.comparison.feed.uplivion')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 p-6">
                    <div className="pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700">{t('landing:differentiation.comparison.content.social')}</span>
                      </div>
                    </div>
                    <div className="pl-4 border-l border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{t('landing:differentiation.comparison.content.uplivion')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 p-6">
                    <div className="pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700">{t('landing:differentiation.comparison.sharing.social')}</span>
                      </div>
                    </div>
                    <div className="pl-4 border-l border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{t('landing:differentiation.comparison.sharing.uplivion')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 p-6">
                    <div className="pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700">{t('landing:differentiation.comparison.organization.social')}</span>
                      </div>
                    </div>
                    <div className="pl-4 border-l border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{t('landing:differentiation.comparison.organization.uplivion')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 p-6">
                    <div className="pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-gray-700">{t('landing:differentiation.comparison.control.social')}</span>
                      </div>
                    </div>
                    <div className="pl-4 border-l border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{t('landing:differentiation.comparison.control.uplivion')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                {t('landing:showcase.title')}
              </h2>
              <p className="text-xl text-gray-600 font-normal max-w-2xl mx-auto">
                {t('landing:showcase.subtitle')}
              </p>
            </div>

            {/* Area 1: Timeline & Memories */}
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto mb-32">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-uplivion-blue/10 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-uplivion-blue" />
                  </div>
                  <span className="text-sm font-semibold text-uplivion-blue uppercase tracking-wider">{t('landing:showcase.timeline.label')}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                  {t('landing:showcase.timeline.title')}
                </h3>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed font-normal">
                  {t('landing:showcase.timeline.description')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-blue rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.timeline.features.organization')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-blue rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.timeline.features.multimedia')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-blue rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.timeline.features.details')}</span>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative">
                <div className="relative bg-gradient-to-br from-uplivion-blue/5 to-uplivion-blue/10 rounded-3xl p-8 backdrop-blur-sm border border-uplivion-blue/10 shadow-2xl">
                  <div className="bg-white/80 rounded-2xl p-6 backdrop-blur-sm shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-uplivion-blue rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-2 bg-gray-200 rounded mb-1"></div>
                        <div className="h-1.5 bg-gray-100 rounded w-3/4"></div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-16 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg"></div>
                      <div className="flex gap-2">
                        <div className="h-8 bg-gray-100 rounded flex-1"></div>
                        <div className="h-8 bg-gray-100 rounded flex-1"></div>
                      </div>
                    </div>
                  </div>
                  <Clock className="absolute top-4 right-4 w-8 h-8 text-uplivion-blue/30" />
                </div>
              </div>
            </div>

            {/* Area 2: Friends & Community */}
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto mb-32">
              <div className="relative">
                <div className="relative bg-gradient-to-br from-uplivion-orange/5 to-uplivion-orange/10 rounded-3xl p-8 backdrop-blur-sm border border-uplivion-orange/10 shadow-2xl">
                  <div className="bg-white/80 rounded-2xl p-6 backdrop-blur-sm shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-uplivion-orange rounded-full"></div>
                        <span className="text-sm font-medium">{t('common:labels.friends')}</span>
                      </div>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="aspect-square bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg"></div>
                      <div className="aspect-square bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg"></div>
                      <div className="aspect-square bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-200 rounded"></div>
                      <div className="h-2 bg-gray-100 rounded w-2/3"></div>
                    </div>
                  </div>
                  <Users className="absolute top-4 right-4 w-8 h-8 text-uplivion-orange/30" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-uplivion-orange/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-uplivion-orange" />
                  </div>
                  <span className="text-sm font-semibold text-uplivion-orange uppercase tracking-wider">{t('landing:showcase.friends.label')}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                  {t('landing:showcase.friends.title')}
                </h3>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed font-normal">
                  {t('landing:showcase.friends.description')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-orange rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.friends.features.invite')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-orange rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.friends.features.shared')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-uplivion-orange rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.friends.features.notifications')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Area 3: Privacy & Security */}
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto mb-32">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Eye className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm font-semibold text-green-600 uppercase tracking-wider">{t('landing:showcase.privacy.label')}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                  {t('landing:showcase.privacy.title')}
                </h3>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed font-normal">
                  {t('landing:showcase.privacy.description')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.privacy.features.levels')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.privacy.features.control')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.privacy.features.groups')}</span>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 relative">
                <div className="relative bg-gradient-to-br from-green-50/50 to-green-100/50 rounded-3xl p-8 backdrop-blur-sm border border-green-200/20 shadow-2xl">
                  <div className="bg-white/80 rounded-2xl p-6 backdrop-blur-sm shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">{t('common:labels.visibility')}</span>
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
                        <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                        <span className="text-sm">{t('common:labels.onlyMe')}</span>
                      </div>
                      <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                        <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        <span className="text-sm">{t('common:labels.forFriends')}</span>
                      </div>
                      <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg opacity-50">
                        <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                        <span className="text-sm">{t('common:labels.publicVisible')}</span>
                      </div>
                    </div>
                  </div>
                  <Eye className="absolute top-4 right-4 w-8 h-8 text-green-600/30" />
                </div>
              </div>
            </div>

            {/* Area 4: Organization & Structure */}
            <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
              <div className="relative">
                <div className="relative bg-gradient-to-br from-purple-50/50 to-purple-100/50 rounded-3xl p-8 backdrop-blur-sm border border-purple-200/20 shadow-2xl">
                  <div className="bg-white/80 rounded-2xl p-6 backdrop-blur-sm shadow-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <Search className="w-5 h-5 text-purple-600" />
                      <div className="flex-1 h-8 bg-gray-100 rounded-lg"></div>
                    </div>
                    <div className="flex gap-2 mb-4">
                      <div className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">#Familie</div>
                      <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">#Reisen</div>
                      <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">#Studium</div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-100 rounded w-4/5"></div>
                      <div className="h-3 bg-gray-100 rounded w-3/5"></div>
                    </div>
                  </div>
                  <Tag className="absolute top-4 right-4 w-8 h-8 text-purple-600/30" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <Search className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">{t('landing:showcase.organization.label')}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                  {t('landing:showcase.organization.title')}
                </h3>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed font-normal">
                  {t('landing:showcase.organization.description')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.organization.features.tags')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.organization.features.search')}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{t('landing:showcase.organization.features.filter')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Modern Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-2">
              <h3 className="text-3xl font-bold mb-4 tracking-tight">uplivion</h3>
              <p className="text-gray-400 text-lg leading-relaxed max-w-md">
                {t('landing:footer.description')}
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('landing:footer.product')}</h4>
              <div className="space-y-3">
                <Link 
                  to={isEnglish ? "/about-en" : "/about"} 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.about')}
                </Link>
                <Link 
                  to={isEnglish ? "/privacy-en" : "/privacy"} 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.privacy')}
                </Link>
                <Link 
                  to={isEnglish ? "/terms-en" : "/terms"} 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.terms')}
                </Link>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('landing:footer.support')}</h4>
              <div className="space-y-3">
                <Link 
                  to={isEnglish ? "/contact-en" : "/contact"} 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.contact')}
                </Link>
                <Link 
                  to="/help" 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.help')}
                </Link>
                <Link 
                  to="/faq" 
                  className="block text-gray-400 hover:text-white transition-colors"
                >
                  {t('landing:footer.links.faq')}
                </Link>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} uplivion. {t('landing:footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>;
};

export default LandingPage;
